import {
  Gas_Compressibility_Options_Enum,
  Well_Gas_Type_Options_Enum
} from "gql/api";
import { beggsBrillCompressibilityCalculator } from "./beggsBrill";
import { californiaNGACompressibilityCalculator } from "./californiaNGA";
import { dranchukCompressibilityCalculator } from "./dranchuk";
import { pengRobinsonCompressibilityCalculator } from "./pengRobinson";
import { poettmanCompressibilityCalculator } from "./poettmanCarpenter";
import type { Correlations } from "@/composables/forms/formTypes";
import type {
  BasicData,
  GasProperties
} from "@/libs/calculations/fluidProperties/d";
import { buildParams } from "../buildParams";
import type { GasVolumeCompressibilityParameters } from "../d";
import type { PsuedoCriticalCalculations } from "../psuedo-criticals";
import { withoutAbsentGasComponents } from "@/libs/calculations/gasPhase/gasTable";

const methodMapGC = {
  [Gas_Compressibility_Options_Enum.BeggsBrill]:
    beggsBrillCompressibilityCalculator,
  [Gas_Compressibility_Options_Enum.Cnga]:
    californiaNGACompressibilityCalculator,
  [Gas_Compressibility_Options_Enum.DranchukPurvis]:
    dranchukCompressibilityCalculator,
  [Gas_Compressibility_Options_Enum.PengRobinson]:
    pengRobinsonCompressibilityCalculator,
  [Gas_Compressibility_Options_Enum.StandingKatz]:
    poettmanCompressibilityCalculator
};

// static/Fluid_Schutff_Gas_Edition.xlsx

export function getGasPhaseVolumeCompressibility(
  basicData: BasicData,
  correlations: Correlations,
  psuedoCriticals: PsuedoCriticalCalculations,
  inSituPressurePSIA: number,
  temperatureF: number,
  gasProperties: GasProperties[],
  dewPointPressurePSIA: number,
  oilFormationVolumeFactorSatBo: number,
  oilFormationVolumeFactorUnSatBo: number,
  oilDensityLBMFT3?: number
): {
  gasCompressibilityFactor: number;
  condensateCompressibilityFactor: number;
  intermediates: {
    [key: string]: unknown;
  };
  params: GasVolumeCompressibilityParameters;
} {
  const gasPhaseVolumeCompressibilityMethod =
    correlations?.gas_compressibility ||
    Gas_Compressibility_Options_Enum.DranchukPurvis;

  const gasCompressibilityCalculator =
    methodMapGC[gasPhaseVolumeCompressibilityMethod];

  const params = buildParams(
    basicData,
    correlations,
    psuedoCriticals,
    inSituPressurePSIA,
    temperatureF,
    gasProperties,
    oilDensityLBMFT3
  );

  const { gasCompressibilityFactor, intermediates } =
    gasCompressibilityCalculator(params);

  let condensateCompressibilityFactor = 0;
  if (basicData.gasType === Well_Gas_Type_Options_Enum.Wet) {
    condensateCompressibilityFactor =
      inSituPressurePSIA > dewPointPressurePSIA
        ? oilFormationVolumeFactorUnSatBo
        : oilFormationVolumeFactorSatBo;
    if (
      correlations?.gas_compressibility ===
      Gas_Compressibility_Options_Enum.PengRobinson
    ) {
      condensateCompressibilityFactor = withoutAbsentGasComponents(
        gasProperties
      ).reduce((sum, g) => sum + g.condensateZ, 0);
    }
  }

  return {
    gasCompressibilityFactor,
    condensateCompressibilityFactor,
    intermediates: intermediates || {},
    params
  };
}
